/**
 * The keyboard half of a segmented control.
 *
 * A `role=radiogroup` of `role=radio` buttons promises the radio pattern: one
 * tab stop for the whole group, and ←/→ (or ↑/↓, Home, End) moving the checked
 * option. Buttons give neither on their own — every one of them is a tab stop
 * and the arrows do nothing — so this wires it on after the group is drawn:
 * the checked option keeps `tabindex=0`, the rest drop to `-1`, and an arrow
 * key presses the neighbour, wrapping at the ends.
 *
 * Pressing is what moves the check, because the group's own click handler is
 * the only thing that knows what a mode means. Groups that repaint themselves
 * from that handler call this again on the new buttons; it is idempotent, and
 * it puts focus back on the option the arrow key chose, since the button that
 * had it no longer exists.
 */

const OPTION = '[role="radio"]';

/** Groups whose keydown listener is already attached. */
const wired = new WeakSet<HTMLElement>();

export function wireSegmented(group: HTMLElement): void {
  const options = [...group.querySelectorAll<HTMLElement>(OPTION)];
  const checkedAt = Math.max(
    0,
    options.findIndex((o) => o.getAttribute('aria-checked') === 'true'),
  );
  options.forEach((option, i) => {
    option.tabIndex = i === checkedAt ? 0 : -1;
  });
  // A repaint took the focused button out of the document with the arrow key
  // still held; the check has moved, so focus follows it.
  if (moving.has(group)) {
    moving.delete(group);
    options[checkedAt]?.focus();
  }
  if (wired.has(group)) return;
  wired.add(group);
  group.addEventListener('keydown', (event) => {
    const step = STEP[event.key];
    if (step === undefined) return;
    const live = [...group.querySelectorAll<HTMLElement>(OPTION)];
    const from = live.indexOf(event.target as HTMLElement);
    if (from === -1) return;
    event.preventDefault();
    const to =
      step === 'first'
        ? 0
        : step === 'last'
          ? live.length - 1
          : (from + step + live.length) % live.length;
    const next = live[to];
    if (!next) return;
    next.focus();
    if (next.getAttribute('aria-checked') === 'true') return;
    moving.add(group);
    next.click();
    // The handler did not repaint, so nothing called back in: settle it here.
    if (moving.delete(group)) wireSegmented(group);
  });
}

/** Groups in the middle of an arrow-key move, waiting for the repaint. */
const moving = new WeakSet<HTMLElement>();

const STEP: Record<string, 1 | -1 | 'first' | 'last' | undefined> = {
  ArrowRight: 1,
  ArrowDown: 1,
  ArrowLeft: -1,
  ArrowUp: -1,
  Home: 'first',
  End: 'last',
};
